import {AttrsSchema, Component} from 'panel';
import mapValues from 'lodash/mapValues';
import findKey from 'lodash/findKey';

import {
  ChartType,
  ChartVizType,
  ValueRepresentationType,
  ValueRepresentationTypeSelection,
} from 'common/report/constants';
import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {SelectTheme} from 'common/components/select/constants';
import {
  DisplayContext,
  OrderedStandardTimeUnits,
  TimeUnit,
  TimeUnitLabels,
} from 'common/widgets/date-range-picker/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {headerDateSelectorRenderer} from 'common/widgets/date-range-preset-bar/header-date-selector';
import {SelectEvent, ToggleTheme} from 'common/components/toggle/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';

import {CHART_VIZ_TYPE_ICONS, ChartTypeSelection} from './constants';
import {template} from './template';
import css from './index.inline.styl';
import {CAPTURED_TRACKING, TrackingEvent} from './tracking';
import {ChartHeaderAttrs, ChartHeaderSelection} from './types';

/* eslint-disable @typescript-eslint/naming-convention */
enum AttributeFields {
  dateRange = `date-range`,
  comparisonDateRange = `comparison-date-range`,
  liftComparison = `selected-lift-comparison`,
  chartType = `selected-chart-type`,
  dateUnit = `selected-date-unit`,
  valueRepresentation = `selected-value-representation`,
  chartVizType = `selected-chart-viz-type`,
}
/* eslint-enable */

export type ChartHeaderUpdateDetail = ChartHeaderSelection & {delta: Partial<ChartHeaderSelection>};

export class ChartHeader extends Component<EmptyObj, ChartHeaderAttrs> {
  static get attrsSchema(): AttrsSchema<ChartHeaderAttrs> {
    return {
      ...super.attrsSchema,
      'max-data-history-days': `number`,
      'date-range': `json`,
      'comparison-date-range': `json`,
      'chart-types': {
        type: `json`,
        default: [],
      },
      'chart-viz-types': {
        type: `json`,
        default: [],
      },
      'chart-type-overrides': {
        type: `json`,
        default: {},
      },
      'date-units': {
        type: `json`,
        default: [],
      },
      'disabled-value-representation': {
        type: `string`,
        enum: Object.values(ValueRepresentationType),
      },
      'disable-date-picker-message': `string`,
      'value-representations': {
        type: `json`,
        default: [],
      },
      'date-unit-overrides': {
        type: `json`,
        default: {},
      },
      'selected-chart-viz-type': {
        type: `string`,
        enum: Object.values(ChartVizType),
      },
      'selected-chart-type': {
        type: `string`,
        enum: Object.values(ChartType),
      },
      'selected-date-unit': {
        type: `string`,
        enum: OrderedStandardTimeUnits,
      },
      breakdowns: `json`,
      'selected-breakdown-segments': `json`,
      'selected-value-representation': {
        type: `string`,
        enum: Object.values(ValueRepresentationType),
        default: ValueRepresentationType.Absolute,
      },
      'context-panel-tooltip-data': `json`,
      'enable-lift-comparison': `boolean`,
      'selected-lift-comparison': `json`,
      'show-date-range-selector': `boolean`,
      'time-comparison-disabled-reason': `string`,
      'time-comparison-tooltip-text': `string`,
      'track-name': `string`,
      'project-id': `number`,
      'workspace-id': `number`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'lift-type-overrides': {
        type: `json`,
        default: {},
        description: `Optional. Allows the outer component to control which LiftTypes are available. All LiftTypes are enabled by default.`,
      },
    };
  }

  private getSelectionObjectFromAttrs(): ChartHeaderSelection {
    return mapValues(AttributeFields, (attribute) => this.attr(attribute));
  }

  public get config() {
    return {
      css,
      template,
      useShadowDom: true,
      helpers: {
        DisplayContext,

        dateRangeSelectorRenderer: () => {
          return headerDateSelectorRenderer({
            displayContext: DisplayContext.FullLength,
            selectedDateRange: this.attr(`date-range`),
            projectAsOfDate: this.attr(`project-as-of-date`),
            maxHistoryDays: this.attr(`max-data-history-days`),
            disableDatePickerMessage: this.attr(`disable-date-picker-message`),
            handleApply: (ev) => this.emitUpdateEvent({dateRange: ev.detail.dateRange}),
          });
        },

        updateChartType: (ev) => {
          const chartType = ev.detail.item.value as ChartType;
          this.track(TrackingEvent.SelectChart, {
            'Chart type': chartType,
          });

          this.emitUpdateEvent({chartType});
        },
        updateValueRepresentation: (ev: SelectEvent) => {
          const valueRepresentation = ev.detail.selected as ValueRepresentationType;
          this.track(TrackingEvent.SelectValueRepresentation, {
            Representatipn: valueRepresentation,
          });

          this.emitUpdateEvent({valueRepresentation});
        },

        updateChartVizType: (ev: SelectEvent) => {
          const chartVizType = ev.detail.selected as ChartVizType;
          this.emitUpdateEvent({chartVizType});
        },
        updateDateUnit: (ev) => {
          const dateUnit = ev.detail.item.value as TimeUnit;
          this.track(TrackingEvent.SelectDateUnit, {
            Unit: dateUnit,
          });

          this.emitUpdateEvent({dateUnit});
        },
        preventTracking: (ev) => ev.stopPropagation(),
        captureTracking: (ev) => {
          const {eventPrefix, props} = ev.detail;
          const eventName = ev.detail.eventName.replace(eventPrefix, ``);

          if (this.isCapturedTrackEvent(eventName)) {
            this.helpers.preventTracking(ev);
            this.track(eventName, props);
          }
        },

        hasSlot: (slotName: string): boolean => {
          return !!this.querySelector(`[slot="${slotName}"]`);
        },

        chartSelectorAttributes: () => {
          return {
            sections: [
              {
                items: this.attr(`chart-types`)
                  .map(
                    (type) =>
                      ChartTypeSelection[type] && {
                        ...ChartTypeSelection[type],
                        isSelected: ChartTypeSelection[type].value === this.attr(`selected-chart-type`),
                        ...(this.attr(`chart-type-overrides`)[type] || {}),
                      },
                  )
                  .filter(Boolean),
              },
            ],
            theme: SelectTheme.Secondary,
            icon: {leftIcon: ChartTypeSelection[this.attr(`selected-chart-type`)].icon},
            'menu-width': 200,
            elref: `chartTypeSelector`,
          };
        },

        dateUnitSelectorAttributes: () => {
          return {
            sections: [
              {
                items: this.attr(`date-units`)
                  .map((value) => ({
                    value,
                    label: TimeUnitLabels[value],
                    isSelected: value === this.attr(`selected-date-unit`),
                    ...(this.attr(`date-unit-overrides`)[value] || {}),
                  }))
                  .filter(Boolean),
              },
            ],
            theme: SelectTheme.Secondary,
            'menu-width': 120,
            elref: `dateUnitSelector`,
          };
        },

        valueRepresentationSelectorAttributes: () => {
          return {
            options: this.attr(`value-representations`).map(
              (type) =>
                ValueRepresentationTypeSelection[type] && {
                  ...ValueRepresentationTypeSelection[type],
                  isSelected:
                    ValueRepresentationTypeSelection[type].value === this.attr(`selected-value-representation`),
                  disabled: ValueRepresentationTypeSelection[type].value === this.attr(`disabled-value-representation`),
                },
            ),
            theme: ToggleTheme.SegmentedController,
            selected: this.attr(`selected-value-representation`),
            elref: `valueRepresentationSelector`,
          };
        },
        getChartVizSelectorAttrs: () => {
          const options = this.attr(`chart-viz-types`).map((val) => ({
            value: val,
            label: ``,
            icon: CHART_VIZ_TYPE_ICONS[val],
            tooltipLabel: `${val} Chart`,
          }));
          return {
            elref: `chartVizSelector`,
            theme: ToggleTheme.SegmentedController,
            options,
            selected: this.attr(`selected-chart-viz-type`),
          };
        },
        getSelectedValueRepresentation: () =>
          ValueRepresentationTypeSelection[
            this.attr(`value-representations`).find(
              (type) => ValueRepresentationTypeSelection[type].value === this.attr(`selected-value-representation`),
            )
          ],
        updateTimeComparison: (ev) => this.emitUpdateEvent({comparisonDateRange: ev.detail}),
        removeComparisonDateRange: () => {
          this.emitUpdateEvent({comparisonDateRange: null});
          this.track(TrackingEvent.RemoveComparisonDateRange);
        },

        updateLiftComparison: (ev: CustomEvent<{liftComparison?: LiftComparison; comparisonDateRange?: DateRange}>) => {
          this.emitUpdateEvent({
            ...ev.detail,
          });
        },
        removeLiftComparison: () => {
          this.emitUpdateEvent({liftComparison: null, comparisonDateRange: null});
          // this.track(TrackingEvent.RemoveComparisonDateRange);
        },

        contextPanelTransform: (x) => {
          return this.attr(`context-panel-tooltip-data`) && this.attr(`context-panel-tooltip-data`)[x.value];
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
  }

  private track(event: string, props: Obj = {}) {
    if (!this.attr(`track-name`)) {
      return;
    }

    if (this.isCapturedTrackEvent(event)) {
      emitTrackEvent(this, {
        eventName: event,
        // Tracking under mp-query-builder for now until we redo eventPrefixes
        eventPrefix: `mp-query-builder`,
        props: {
          ...props,
          project_id: this.attr(`project-id`),
          tab: this.attr(`track-name`),
          source: `chart-header`,
        },
        cancelable: true,
      });
    } else {
      throw new Error(`tracking event not defined: ${event}`);
    }
  }

  private isCapturedTrackEvent(event: string) {
    return findKey(TrackingEvent, (eventName) => eventName === event) || CAPTURED_TRACKING.has(event);
  }

  private emitUpdateEvent(updates: Partial<ChartHeaderSelection>) {
    const existingSelection = this.getSelectionObjectFromAttrs();
    const selection = {...existingSelection, ...updates};

    const detail: ChartHeaderUpdateDetail = {
      ...selection,
      delta: updates,
    };

    // TODO: dispatch discreet events for different changes.
    // Emitting one event requires event handlers break open the details to figure out what actually happened
    // Considering that we know what happened in the methods that call this one,
    // it's silly that we lose that detail when bubbling up. I suspect this event has been co-opted to mean more things over time.
    this.dispatchEvent(
      new CustomEvent<ChartHeaderUpdateDetail>(`update`, {detail}),
    );
  }

  showLiftComparison() {
    return this.attr(`enable-lift-comparison`);
  }
}

export const ChartHeaderEl = defineAndMakeJsxFactory(`mp-chart-header`, ChartHeader);
export const ChartHeaderReact = panel2react(`mp-chart-header`, ChartHeader);
